import React, {
  useReducer,
  useEffect,
  useContext,
  ComponentType,
  useState
} from "react";

import { fetchBooksAndChapters, fetchBibles } from "../../api/BibleApi";
import { Bible, Book, AudioBible } from "api/typedefs/Bible";
import { Site } from "api/typedefs/Site";
import { useScriptureRouting } from "./useScriptureRouting";
import { useServiceWorker } from "components/Site/ServiceWorkerContext";

export interface StateBible extends Bible {
  orderedChapterIds?: string[];
  books?: Book[];
}
interface StateBiblesById {
  [bibleId: string]: StateBible | undefined;
}

export interface BibleCtx {
  biblesById: StateBiblesById;
  bibleIds: string[];
  complete: boolean;
}

interface BibleAction {
  type: string;
  payload?: any;
}

const buildChapterIdArray = (books: Book[] | undefined): string[] =>
  (books || []).reduce(
    (acc, book) =>
      acc.concat(
        book.chapters
          .sort((a, b) => a.position - b.position)
          .map(chapter => chapter.id)
      ),
    [] as string[]
  );


export const defaultBibleContext: BibleCtx = {
  biblesById: {},
  bibleIds: [],
  complete: false
};

const bibleReducer = (state: BibleCtx, action: BibleAction) => {
  switch (action.type) {
    case "LOAD_BIBLES": {
      const bibleIds = action.payload.data.map(({ id }: { id: string }) => id);
      const biblesById = action.payload.data.reduce(
        (acc: StateBiblesById, bible: StateBible) => {
          acc[bible.id] = {
            ...state.biblesById[bible.id],
            ...bible
          };
          return acc;
        },
        {} as StateBiblesById
      );
      return {
        ...state,
        biblesById,
        bibleIds: [...new Set([...state.bibleIds, ...bibleIds])],
        complete: true
      };
    }
    case "UPDATE_BOOKS_AND_CHAPTERS": {
      return {
        ...state,
        biblesById: {
          ...state.biblesById,
          [action.payload.bibleId]: {
            ...state.biblesById[action.payload.bibleId],
            books: action.payload.books,
            orderedChapterIds: buildChapterIdArray(action.payload.books)
          }
        } as StateBiblesById
      };
    }
    case "UPDATE_AUDIO_BOOKS_AND_CHAPTERS": {
      return {
        ...state,
        biblesById: {
          ...state.biblesById,
          [action.payload.bibleId]: {
            ...state.biblesById[action.payload.bibleId],
            audioBibles: action.payload.audioBibles,
          }
        } as StateBiblesById
      };
    }
    default:
      return { ...defaultBibleContext };
  }
};

export const BibleContext = React.createContext(defaultBibleContext);

// TODO: need to handle error states in Bible Provider....
export const BibleProvider = ({
  site,
  children
}: {
  site: Site;
  children: any;
}) => {
  const [siteBibleIds] = useState(site.bibles.map(({ id }) => id));
  const [siteBiblesById] = useState(
    site.bibles.reduce((acc, bible) => {
      acc[bible.id] = bible;
      return acc;
    }, {} as { [bibleId: string]: StateBible })
  );
  const [state, dispatch] = useReducer(bibleReducer, {
    ...defaultBibleContext,
    biblesById: siteBiblesById,
    bibleIds: siteBibleIds
  });

  const { hasActiveSW } = useServiceWorker();
  const { bibleId } = useScriptureRouting();

  // loadBibles TODO: handle error cases
  useEffect(() => {
    // avoid any data fetching if:
    // - all the bible data we need is available on site config
    // - we've already fetch all the bibles
    if (!site.includeAllBibles || state.complete) return;

    // NOTE: As it stands, that cache is network-first, so subsequest requests
    // will have to wait until that request completes. However the user won't be
    // loading all JS resources, so it should complete much faster than an initial
    // load and the requests is still backed by cache in the case they're offline.
    // HACK: wait just long enough to be sure to not prioritize over
    // chapters requests if service worker is not present
    setTimeout(
      () => {
        fetchBibles([], site.includeAllBibles).then(payload => {
          dispatch({ type: "LOAD_BIBLES", payload });
        });
      },
      hasActiveSW ? 0 : 1000
    );
  }, [state.complete, site.includeAllBibles, hasActiveSW]);

  // loadBooksAndChapters
  const bible: Bible = state.biblesById[bibleId];
  const bibleHasBooksLoaded = Boolean(bible?.books);
  useEffect(() => {
    if (bibleHasBooksLoaded) return;
    fetchBooksAndChapters({ bibleId }).then(({ data: books }) => {
      dispatch({
        type: "UPDATE_BOOKS_AND_CHAPTERS",
        payload: { bibleId, books }
      });
    });
  }, [bibleId, bibleHasBooksLoaded]);

  // loadBooksAndChapters for audio Bibles
  const audioBibles = bible?.audioBibles ?? [];
  const audioBiblesHaveBooksLoaded = audioBibles.every((audioBible: AudioBible) => Boolean(audioBible.books));
  useEffect(() => {
    if (audioBiblesHaveBooksLoaded) return;
    const promises = audioBibles.map( 
      audioBible => fetchBooksAndChapters({ bibleId: audioBible.id, bibleType: 'audio' })
        .then(({ data: books}) => { return { ...audioBible,  books } } )
    );

    Promise.all(promises).then( audioBibles => {
      dispatch({
        type: "UPDATE_AUDIO_BOOKS_AND_CHAPTERS",
        payload: { bibleId, audioBibles  }
      });
    });
  }, [bibleId, audioBibles, audioBiblesHaveBooksLoaded]);

  return (
    <BibleContext.Provider value={state}>{children}</BibleContext.Provider>
  );
};

export const useBible = () => useContext(BibleContext);

export function withBible<Props extends { bible: BibleCtx }>(
  C: ComponentType<Props>
) {
  return function ComponentWithBible(props: any) {
    return (
      <BibleContext.Consumer>
        {bible => <C {...props} bible={bible} />}
      </BibleContext.Consumer>
    );
  };
}

export default BibleProvider;
